import React from 'react';
import { legacyApi, WorkflowEventDefinition, WorkflowPrecheckResponse } from '../../../services/legacyApi';
import { Button } from '../../../components/ui/button';
import { FlowPrecheckDialog } from '../../../components/workflow/FlowPrecheckDialog';

type TransitionActionBarProps = {
    actions: WorkflowEventDefinition[];
    onAction: (eventCode: string) => Promise<void> | void;
    disabled?: boolean;
    busyEventCode?: string | null;
    precheckEntity?: string;
    precheckEntityId?: number;
    hiddenEventCodes?: string[];
    actionOrder?: string[];
};

export const TransitionActionBar: React.FC<TransitionActionBarProps> = ({
    actions,
    onAction,
    disabled = false,
    busyEventCode = null,
    precheckEntity,
    precheckEntityId,
    hiddenEventCodes = [],
    actionOrder = [],
}) => {
    const [precheckBusyCode, setPrecheckBusyCode] = React.useState<string | null>(null);
    const [pendingActionCode, setPendingActionCode] = React.useState<string | null>(null);
    const [precheckResult, setPrecheckResult] = React.useState<WorkflowPrecheckResponse | null>(null);
    const [precheckOpen, setPrecheckOpen] = React.useState(false);

    const hiddenSet = React.useMemo(
        () => new Set((hiddenEventCodes || []).map((code) => String(code || '').trim().toLowerCase())),
        [hiddenEventCodes],
    );
    const orderedActions = React.useMemo(() => {
        const visible = (actions || []).filter((action) => !hiddenSet.has(String(action.event_code || '').trim().toLowerCase()));
        if (!visible.length) return visible;
        const orderMap = new Map<string, number>();
        (actionOrder || []).forEach((code, index) => orderMap.set(String(code || '').trim().toLowerCase(), index));
        return [...visible].sort((a, b) => {
            const aCode = String(a.event_code || '').trim().toLowerCase();
            const bCode = String(b.event_code || '').trim().toLowerCase();
            const aPriority = orderMap.has(aCode) ? Number(orderMap.get(aCode)) : 50;
            const bPriority = orderMap.has(bCode) ? Number(orderMap.get(bCode)) : 50;
            if (aPriority !== bPriority) return aPriority - bPriority;
            if (aCode === 'cancel' && bCode !== 'cancel') return 1;
            if (bCode === 'cancel' && aCode !== 'cancel') return -1;
            return aCode.localeCompare(bCode);
        });
    }, [actions, hiddenSet, actionOrder]);

    if (!orderedActions.length) {
        return <p className="text-sm text-muted-foreground">No further actions available for this status.</p>;
    }

    const runOrPrecheck = async (eventCode: string) => {
        if (!precheckEntity || !precheckEntityId) {
            await onAction(eventCode);
            return;
        }

        setPrecheckBusyCode(eventCode);
        try {
            const result = await legacyApi.workflow.precheck({
                entity: precheckEntity,
                entity_id: precheckEntityId,
                action: eventCode,
            });
            setPrecheckResult(result);
            const overall = String(result.overall_status || 'pass').toLowerCase();
            if (overall === 'pass') {
                await onAction(eventCode);
                return;
            }
            setPendingActionCode(eventCode);
            setPrecheckOpen(true);
        } finally {
            setPrecheckBusyCode(null);
        }
    };

    return (
        <>
            <div className="flex flex-wrap gap-2">
                {orderedActions.map((action) => {
                    const isBusy = busyEventCode === action.event_code || precheckBusyCode === action.event_code;
                    return (
                        <Button
                            key={action.event_code}
                            variant={action.event_code === 'cancel' ? 'destructive' : 'default'}
                            size="sm"
                            disabled={disabled || isBusy}
                            onClick={() => runOrPrecheck(action.event_code)}
                            title={action.event_description || action.event_label}
                        >
                            {isBusy ? 'Working...' : action.event_label}
                        </Button>
                    );
                })}
            </div>
            <FlowPrecheckDialog
                open={precheckOpen}
                result={precheckResult}
                onClose={() => {
                    setPrecheckOpen(false);
                    setPendingActionCode(null);
                }}
                onContinue={() => {
                    const actionCode = pendingActionCode;
                    setPrecheckOpen(false);
                    setPendingActionCode(null);
                    if (actionCode) {
                        void onAction(actionCode);
                    }
                }}
                continueDisabled={!!busyEventCode}
            />
        </>
    );
};

